class CheckboxUpsell extends HTMLElement {
  constructor() {
    super();
    this.isProcessing = false;
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    }
  }

  async handleCheckboxChange(event) {
    const checkbox = event.target;
    const isChecked = checkbox.checked;
    
    // Prevent multiple simultaneous operations
    if (this.isProcessing) {
      console.log('Already processing, please wait...');
      checkbox.checked = !isChecked; // Revert checkbox state
      return;
    }
    
    this.setProcessing(true);
    
    // Get product data from JSON
    const jsonElement = this.querySelector('.json-selling');
    if (!jsonElement || !jsonElement.textContent) {
      console.error('No product data found');
      this.setProcessing(false);
      return;
    }

    try {
      const productData = JSON.parse(jsonElement.textContent);
      const variantId = productData.variants?.[0]?.id;
      
      if (!variantId) {
        console.error('No variant ID found');
        this.setProcessing(false);
        return;
      }

      console.log('Product ID from JSON:', productData.id);
      console.log('Variant ID:', variantId, 'Checked:', isChecked);

      if (isChecked) {
        await this.addToCart(variantId);
      } else {
        await this.removeFromCart(variantId);
      }
      
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.setProcessing(false);
    }
  }

  async addToCart(variantId) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1
          }],
          sections: this.getSectionsToRender().map(section => section.section),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');
      
      const parsedState = await response.json();
      console.log('Added to cart:', parsedState);
      
      this.updateSections(parsedState);
      this.publishCartUpdate('product-form', parsedState, variantId);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async removeFromCart(variantId) {
    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();
      
      const cartItem = cart.items.find(item => item.variant_id === variantId);
      if (!cartItem) {
        console.log('Item not in cart');
        return;
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: cartItem.key,
          quantity: 0,
          sections: this.getSectionsToRender().map(section => section.section),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Failed to remove from cart');
      
      const parsedState = await response.json();
      console.log('Removed from cart:', parsedState);
      
      this.updateSections(parsedState);
      this.publishCartUpdate('cart-items', parsedState, variantId);
      
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  setProcessing(isProcessing) {
    this.isProcessing = isProcessing;
    
    // Disable/enable all checkout buttons
    const checkoutButtons = document.querySelectorAll('.cart__checkout-button');
    checkoutButtons.forEach(button => {
      if (isProcessing) {
        button.setAttribute('disabled', 'disabled');
      } else {
        button.removeAttribute('disabled');
      }
    });
    
    // Also disable our own checkbox during processing
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (checkbox) {
      if (isProcessing) {
        checkbox.setAttribute('disabled', 'disabled');
      } else {
        checkbox.removeAttribute('disabled');
      }
    }
    
    // Add loading state visual feedback
    const toggleKnob = this.querySelector('.toggle-knob');
    if (toggleKnob) {
      if (isProcessing) {
        toggleKnob.classList.add('processing');
      } else {
        toggleKnob.classList.remove('processing');
      }
    }
  }

  getSectionsToRender() {
    const sections = [];
    
    const cartDrawerItems = document.getElementById('CartDrawer');
    if (cartDrawerItems) {
      sections.push({
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      });
    }
    
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset?.id || 'main-cart-items',
        selector: '.js-contents',
      });
    }
    
    sections.push(
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    );
    
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset?.id || 'main-cart-footer',
        selector: '.js-contents',
      });
    }
    
    return sections;
  }

  updateSections(parsedState) {
    if (!parsedState.sections) return;
    
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections[section.section]) return;
      
      const elementToReplace = 
        document.getElementById(section.id)?.querySelector(section.selector) || 
        document.getElementById(section.id);
        
      if (elementToReplace) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  publishCartUpdate(source, cartData, variantId) {
    const event = new CustomEvent('cart-update', {
      bubbles: true,
      detail: {
        source: source,
        cartData: cartData,
        variantId: variantId
      }
    });
    document.dispatchEvent(event);
    
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      publish(window.PUB_SUB_EVENTS.cartUpdate, { 
        source: source,
        cartData: cartData,
        variantId: variantId
      });
    }
  }
}

// Define the custom element
if (!customElements.get('checkbox-upsell')) {
  customElements.define('checkbox-upsell', CheckboxUpsell);
}